import { api, API_BASE_URL, snakeizeKeys } from '@shared/api';
import {
  buildNotificationFromSse,
  parseCardEventMeta,
  type SseCardEventMeta,
} from './notificationsApi';
import type { NotificationDto } from './types';

interface StreamHandlers {
  onNotification: (n: NotificationDto) => void;
  onCardEvent?: (e: SseCardEventMeta) => void;
  /** `generationUpdated` — сводка записи «Генераций» после события карточки; данные уже в
   *  snake_case, как REST после интерцептора (один разборщик — `parseGenerationEvent`). */
  onGenerationUpdated?: (payload: unknown) => void;
  onOpen?: () => void;
  onError?: (err: unknown) => void;
}

interface SseTicketResponse {
  ticket: string;
}

const INITIAL_DELAY_MS = 1000;
const MAX_DELAY_MS = 10_000;
// Backend шлёт `: heartbeat` каждые 25s. Если за 35s не пришло ничего
// (включая комментарии), считаем коннект протухшим — fetch может «висеть»
// на reader.read() и не замечать рестарт сервера (типичная проблема SSE через
// fetch+ReadableStream на Windows и Vite proxy).
const IDLE_TIMEOUT_MS = 35_000;

export class NotificationsStream {
  private controller: AbortController | null = null;
  private reconnectTimer: number | null = null;
  private idleTimer: number | null = null;
  private retryDelay = INITIAL_DELAY_MS;
  private stopped = false;
  private wasConnected = false;
  private getToken: () => string | null;
  private handlers: StreamHandlers;
  private visibilityHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;

  constructor(getToken: () => string | null, handlers: StreamHandlers) {
    this.getToken = getToken;
    this.handlers = handlers;
  }

  start(): void {
    // Один живой поток на вкладку. Осиротевший поток (горячая перезагрузка модулей в
    // dev, повторный монтаж слушателя) держит свою SSE-подписку, а Redis доставляет
    // событие в каждую — у каждого слушателя свой набор увиденных id, и одно
    // уведомление превращалось в несколько одинаковых тостов (down, 2026-09-23).
    const registry = window as unknown as { __lxNotificationsStream?: NotificationsStream };
    if (registry.__lxNotificationsStream && registry.__lxNotificationsStream !== this) {
      registry.__lxNotificationsStream.stop();
    }
    registry.__lxNotificationsStream = this;
    this.stopped = false;
    this.attachWindowHandlers();
    void this.connect();
  }

  stop(): void {
    const registry = window as unknown as { __lxNotificationsStream?: NotificationsStream };
    if (registry.__lxNotificationsStream === this) delete registry.__lxNotificationsStream;
    this.stopped = true;
    this.controller?.abort();
    this.controller = null;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearIdleTimer();
    this.detachWindowHandlers();
  }

  private clearIdleTimer(): void {
    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private armIdleTimer(): void {
    this.clearIdleTimer();
    if (this.stopped) return;
    this.idleTimer = window.setTimeout(() => {
      this.idleTimer = null;
      // Прерываем fetch — readStream получит AbortError, попадёт в finally
      // и через scheduleReconnect поднимет соединение заново.
      this.controller?.abort();
    }, IDLE_TIMEOUT_MS);
  }

  private attachWindowHandlers(): void {
    // Когда юзер вернулся в таб или сеть появилась — форсим немедленный реконнект
    // (типичный кейс: рестарт бэка, пробуждение лэптопа).
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && !this.controller) this.kickReconnect();
    };
    // Симметрично visibility: реконнектим по 'online' только когда соединения нет —
    // иначе дёргали бы здоровый стрим (а single-flight connect его ещё и оборвёт).
    this.onlineHandler = () => {
      if (!this.controller) this.kickReconnect();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('online', this.onlineHandler);
  }

  private detachWindowHandlers(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
  }

  private kickReconnect(): void {
    if (this.stopped) return;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.retryDelay = INITIAL_DELAY_MS;
    void this.connect();
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    // Если соединение уже было установлено — сбрасываем backoff, чтобы рестарт
    // бэка не приводил к 30-секундным окнам без SSE.
    if (this.wasConnected) {
      this.retryDelay = INITIAL_DELAY_MS;
      this.wasConnected = false;
    }
    const delay = this.retryDelay;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.retryDelay = Math.min(this.retryDelay * 2, MAX_DELAY_MS);
      void this.connect();
    }, delay);
  }

  private async fetchTicket(): Promise<string | null> {
    if (!this.getToken()) return null;
    try {
      // Идём через axios `api` — у него есть интерцептор с авто-refresh access-токена,
      // поэтому 401 на /sse-ticket превратится в /auth/refresh + retry прозрачно.
      const { data } = await api.post<SseTicketResponse>('/notification/sse-ticket');
      return data?.ticket ?? null;
    } catch {
      return null;
    }
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;

    // Single-flight. connect() реентерабелен (online-хендлер, реконнект-таймер,
    // async-gap внутри fetchTicket). Раньше каждый повторный вход создавал НОВЫЙ
    // controller, осиротив прошлый fetch: тот продолжал читать поток (readStream
    // крутится на while(!stopped)) и держал лишнюю SSE-подписку на канале юзера.
    // Redis fan-out доставлял один publish во ВСЕ живые подписки → дубль-тосты.
    // Гасим прошлое соединение и владеем СВОИМ controller'ом весь вызов.
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;

    const ticket = await this.fetchTicket();
    // Пока ждали тикет, мог войти новый connect() (стал текущим) или нас остановили —
    // тихо выходим, не трогая общий стейт и не плодя реконнектов.
    if (this.stopped || this.controller !== controller) {
      controller.abort();
      return;
    }
    if (!ticket) {
      this.controller = null;
      this.scheduleReconnect();
      return;
    }

    try {
      const url = `${API_BASE_URL}/notification/stream?ticket=${encodeURIComponent(ticket)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'text/event-stream' },
        credentials: 'include',
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`SSE response not ok: ${response.status}`);
      }

      this.retryDelay = INITIAL_DELAY_MS;
      this.wasConnected = true;
      this.armIdleTimer();
      this.handlers.onOpen?.();
      await this.readStream(response.body);
    } catch (err) {
      if ((err as { name?: string })?.name !== 'AbortError') {
        this.handlers.onError?.(err);
      }
    } finally {
      this.clearIdleTimer();
      // Чистим/реконнектим ТОЛЬКО если мы всё ещё текущее соединение — иначе нас уже
      // заменил новый connect() (тогда это его controller/реконнект, не трогаем).
      if (this.controller === controller) {
        this.controller = null;
        this.scheduleReconnect();
      }
    }
  }

  private async readStream(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (!this.stopped) {
      const { value, done } = await reader.read();
      if (done) return;
      // Любой байт (включая heartbeat-комментарий) продлевает watchdog.
      this.armIdleTimer();
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const rawEvent = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        this.handleSseBlock(rawEvent);
        boundary = buffer.indexOf('\n\n');
      }
    }
  }

  private handleSseBlock(block: string): void {
    if (!block.trim()) return;
    let event = 'message';
    const dataLines: string[] = [];

    for (const line of block.split('\n')) {
      if (!line || line.startsWith(':')) continue;
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    }

    if (event === 'ping' || dataLines.length === 0) return;

    let payload: unknown;
    try {
      payload = JSON.parse(dataLines.join('\n'));
    } catch {
      return;
    }

    // Живое событие записи «Генераций» (контракт § 8): в колокольчик и счётчик не идёт.
    // SSE идёт мимо axios — ключи приводим к snake_case здесь, чтобы разборщик был один.
    if (event === 'generationUpdated') {
      this.handlers.onGenerationUpdated?.(snakeizeKeys(payload));
      return;
    }

    const meta = parseCardEventMeta(event, payload);
    if (meta) {
      this.handlers.onCardEvent?.(meta);
    }

    const fromEvent = buildNotificationFromSse(event, payload);
    if (fromEvent) {
      this.handlers.onNotification(fromEvent);
    }
  }
}
